export interface SessionUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
  permissions: string[];
  mustChangePassword: boolean;
  source: "local" | "shopify";
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly details?: unknown) {
    super(message);
  }
}

let csrfToken = sessionStorage.getItem("razzi_ops_csrf") || "";

export function setCsrfToken(value: string | null | undefined) {
  csrfToken = value || "";
  if (csrfToken) sessionStorage.setItem("razzi_ops_csrf", csrfToken);
  else sessionStorage.removeItem("razzi_ops_csrf");
}

async function shopifyToken() {
  const shopify = (window as Window & { shopify?: { idToken?: () => Promise<string> } }).shopify;
  try {
    return shopify?.idToken ? await shopify.idToken() : null;
  } catch {
    return null;
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await shopifyToken();
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (csrfToken && !["GET", "HEAD"].includes(options.method || "GET")) headers.set("x-csrf-token", csrfToken);
  const response = await fetch(path, { ...options, headers, credentials: "include" });
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    const message = typeof payload === "object" && payload && "message" in payload ? String(payload.message) : `Request failed (${response.status}).`;
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

export function get<T>(path: string) {
  return api<T>(path);
}

export function post<T>(path: string, body?: unknown) {
  return api<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
}

export function patch<T>(path: string, body: unknown) {
  return api<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}

export async function currentSession(): Promise<{ user: SessionUser; csrfToken?: string } | null> {
  try {
    const session = await get<{ user: SessionUser; csrfToken?: string }>("/auth/me");
    setCsrfToken(session.csrfToken);
    return session;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}
